// src/pages/ContactUsMessage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import logo from "../assets/logo.png";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// ✅ Env base
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// ✅ Axios instance with token
const api = axios.create({
  baseURL: API_BASE_URL,
});
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const ContactUsMessage = () => {
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // =============================
  // Fetch messages
  // =============================
  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError("");
      const { data } = await api.get(`/home/contact-messages`);

      // merge with locally saved status
      const statusMap = JSON.parse(localStorage.getItem("contactStatus") || "{}");

      const merged = (Array.isArray(data) ? data : []).map((msg) => {
        const id = msg?.id ?? msg?.contact_id ?? msg?._id;
        return {
          ...msg,
          status: statusMap[id] || msg.status || "Unread",
        };
      });

      setMessages(merged);
    } catch (err) {
      console.error("Error fetching contact messages:", err);
      setError("Failed to load messages.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  // =============================
  // Helpers
  // =============================
  const getId = (row) => row?.id ?? row?.contact_id ?? row?._id;
  const safe = (v) => (v == null || v === "" ? "-" : v);
  const formatDate = (v) => {
    try {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return "-";
      return d.toLocaleString();
    } catch {
      return "-";
    }
  };

  // =============================
  // Actions
  // =============================

  // ⛔ No backend update. Local toggle + persistence.
  const handleToggleStatus = async (msg) => {
    const id = getId(msg);
    if (!id) return;

    const current = msg.status || "Unread";
    const nextStatus = current === "Replied" ? "Unread" : "Replied";
    if (!window.confirm(`Mark this message as "${nextStatus}"?`)) return;

    // save to localStorage
    const statusMap = JSON.parse(localStorage.getItem("contactStatus") || "{}");
    statusMap[id] = nextStatus;
    localStorage.setItem("contactStatus", JSON.stringify(statusMap));

    // update UI
    setMessages((prev) =>
      prev.map((m) =>
        getId(m) === id ? { ...m, status: nextStatus } : m
      )
    );
  };

  const handleDelete = async (msg) => {
    const id = getId(msg);
    if (!id) return;
    if (!window.confirm("Delete this message?")) return;

    try {
      setLoading(true);
      setError("");
      await api.delete(`/home/contact-messages/${id}`);
      await fetchMessages();
    } catch (err) {
      console.error("Error deleting contact message:", err);
      setError("Failed to delete.");
    } finally {
      setLoading(false);
    }
  };

  // =============================
  // Export to Excel
  // =============================
  const handleExportExcel = async () => {
    if (!messages || messages.length === 0) {
      alert("No messages to export!");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Contact Messages");

    worksheet.columns = [
      { header: "S.No", key: "sno", width: 10 },
      { header: "Name", key: "name", width: 25 },
      { header: "Email", key: "email", width: 30 },
      { header: "Phone", key: "phone", width: 20 },
      { header: "Message", key: "message", width: 50 },
      { header: "Status", key: "status", width: 15 },
      { header: "Created At", key: "created_at", width: 25 },
    ];

    messages.forEach((msg, index) => {
      worksheet.addRow({
        sno: index + 1,
        name: msg.name || "-",
        email: msg.email || "-",
        phone: msg.phone || msg.phone_number || "-",
        message: msg.message || "-",
        status: msg.status || "Unread",
        created_at: formatDate(msg.created_at || msg.createdAt),
      });
    });

    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF2563EB" },
    };

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(
      blob,
      `Contact_Messages_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  // =============================
  // Derived
  // =============================
  const filtered = useMemo(() => {
    if (filter === "All") return messages;
    return messages.filter((m) => (m.status || "Unread") === filter);
  }, [messages, filter]);

  // =============================
  // JSX
  // =============================
  return (
       <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 py-3">
          <img
            src={logo}
            alt="HPR Infra Logo"
            className="h-10 sm:h-12 md:h-14 w-auto object-contain"
          />
          <button
            onClick={() => navigate("/dashboard")}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium shadow-sm transition-all text-sm sm:text-base"
          >
            Back to Dashboard
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h1 className="text-2xl sm:text-3xl font-semibold text-blue-700">
            Home Contact Messages
          </h1>

          <div className="flex items-center gap-3">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-gray-700 focus:border-blue-500 outline-none text-sm sm:text-base"
            >
              <option value="All">All</option>
              <option value="Unread">Unread</option>
              <option value="Replied">Replied</option>
            </select>

            <button
              onClick={fetchMessages}
              disabled={loading}
              className={`px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition ${
                loading
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-gray-100 text-gray-800 hover:bg-gray-200"
              }`}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>

            {/* ✅ New Print (Excel) Button */}
            <button
              onClick={handleExportExcel}
              disabled={loading || messages.length === 0}
              className={`px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition ${
                loading || messages.length === 0
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-blue-100 text-blue-700 hover:bg-blue-200"
              }`}
            >
              Download Excel File
            </button>

            {/* ✅ Email Settings Button */}
            <button
              onClick={() => navigate('/dashboard/email-settings')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium shadow-sm transition-all text-sm sm:text-base hover:bg-blue-700"
            >
              Email Settings
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3">
            {error}
          </div>
        )}

        <div className="bg-white border border-gray-100 rounded-2xl shadow-md hover:shadow-lg transition-all overflow-hidden">
          <table className="w-full text-left text-sm sm:text-base">
            <thead className="bg-green-600 text-white">
              <tr>
                <th className="px-4 sm:px-6 py-3 font-semibold">Name</th>
                <th className="px-4 sm:px-6 py-3 font-semibold">Email</th>
                <th className="px-4 sm:px-6 py-3 font-semibold">Phone</th>
                <th className="px-4 sm:px-6 py-3 font-semibold">Message</th>
                <th className="px-4 sm:px-6 py-3 font-semibold text-center">
                  Status
                </th>
                <th className="px-4 sm:px-6 py-3 font-semibold">Date</th>
                <th className="px-4 sm:px-6 py-3 font-semibold text-center">
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center text-gray-500 py-8">
                    Loading messages...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center text-gray-500 py-8">
                    No messages found.
                  </td>
                </tr>
              ) : (
                filtered.map((m, i) => {
                  const id = getId(m) ?? i;
                  const status = m.status || "Unread";
                  return (
                    <tr
                      key={id}
                      className="border-t border-gray-100 hover:bg-gray-50 transition"
                    >
                      <td className="px-4 sm:px-6 py-3 font-medium text-gray-800">
                        {safe(m.name)}
                      </td>
                      <td className="px-4 sm:px-6 py-3 text-gray-700">
                        {safe(m.email)}
                      </td>
                      <td className="px-4 sm:px-6 py-3 text-gray-700">
                      {safe(m.phone ?? m.phone_number ?? m.mobile ?? m.contact_number)}
                      </td>
                      <td className="px-4 sm:px-6 py-3 text-gray-700 max-w-[420px]">
                        <span title={m.message}>{safe(m.message)}</span>
                      </td>
                      <td className="px-4 sm:px-6 py-3 text-center">
                        <span
                          className={`inline-block px-3 py-1 text-sm font-semibold rounded-full shadow-sm ${
                            status === "Replied"
                              ? "text-green-700 bg-green-100"
                              : "text-yellow-700 bg-yellow-100"
                          }`}
                        >
                          {status}
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-3 text-gray-500">
                        {formatDate(m.created_at ?? m.createdAt)}
                      </td>
                      <td className="px-4 sm:px-6 py-3 text-center">
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={() => handleToggleStatus(m)}
                            disabled={loading}
                            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition ${
                              loading
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : status === "Replied"
                                ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                                : "bg-green-100 text-green-700 hover:bg-green-200"
                            }`}
                          >
                            {status === "Replied"
                              ? "Mark Unread"
                              : "Mark Replied"}
                          </button>

                          <button
                            onClick={() => handleDelete(m)}
                            disabled={loading}
                            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition ${
                              loading
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : "bg-rose-100 text-rose-700 hover:bg-rose-200"
                            }`}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

export default ContactUsMessage;